import React, { useEffect, useState, useMemo } from "react";
import api from "../../services/apiClient.ts";
import { toast } from 'react-toastify';
import { useTranslation } from "react-i18next";
import { Plus, Search, Pencil, Trash2, Loader, Receipt } from "lucide-react";
import ConfirmationModal from '../common/ConfirmationModal';
import useConfirmation from '../../hooks/useConfirmation';

const API_BASE = "/v1";

export default function GeneralPaymentsPage() {
  const { t } = useTranslation();
  const { confirmationState, hideConfirmation, confirmDelete } = useConfirmation();
  const branchId = localStorage.getItem("branch") ?? "1";

  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ id: null, name: "" });
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`${API_BASE}/general-payments`, {
        params: { q, branch_id: branchId },
      });
      setItems(r.data?.data ?? r.data?.data?.data ?? r.data?.data ?? r.data ?? []);
    } catch (e) {
      console.error(e);
      toast.error(t("generalPayments.loadFailed"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchId]);

  const filteredItems = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return items;
    return items.filter(it => (it?.name ?? "").toLowerCase().includes(query));
  }, [items, q]);

  function openAdd() { setForm({ id: null, name: "" }); setShowModal(true); }
  function openEdit(row) { setForm({ id: row.id, name: row.name }); setShowModal(true); }
  function close() { setShowModal(false); }

  async function save() {
    try {
      if (!form.name.trim()) return toast.error(t("generalPayments.nameRequired"));
      if (!form.id) {
        await api.post(`${API_BASE}/general-payments`, { name: form.name });
        toast.success(t("generalPayments.added"));
      } else {
        await api.put(`${API_BASE}/general-payments/${form.id}`, { name: form.name });
        toast.success(t("generalPayments.updated"));
      }
      close();
      load();
    } catch (e) {
      const msg = e?.response?.data?.message || e.message;
      toast.error(msg || t("generalPayments.saveFailed"));
    }
  }

  function handleRemove(row) {
    confirmDelete(row?.name || t("generalPayments.title"), async () => {
      try {
        await api.delete(`${API_BASE}/general-payments/${row.id}`);
        toast.success(t("generalPayments.deleted"));
        load();
      } catch (e) {
        toast.error(e?.response?.data?.message || t("generalPayments.deleteFailed"));
      }
    });
  }

  return (
    <div className="text-gray-900 dark:text-gray-100 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header Card - نفس ستايل ManageGroups */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-reverse space-x-3">
                <div className="bg-white/20 rounded-full p-3">
                  <Receipt className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">{t("generalPayments.title")}</h2>
                  <p className="text-blue-100 mt-1">{t("generalPayments.subtitle")}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative w-56 sm:w-64">
                  <Search className="absolute left-3 rtl:right-3 rtl:left-auto top-1/2 transform -translate-y-1/2 text-white/80 h-5 w-5" />
                  <input
                    type="text"
                    placeholder={t("generalPayments.searchPlaceholder")}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="w-full pl-10 rtl:pr-10 rtl:pl-4 pr-4 py-2.5 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-200"
                  />
                </div>
                <button
                  onClick={openAdd}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 rounded-xl text-white font-medium transition-all duration-200"
                >
                  <Plus className="w-5 h-5" />
                  {t("generalPayments.addNew")}
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="p-4 sm:p-6">
            <div className="app-table-wrap">
            <table className="app-table min-w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-600">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap w-16">#</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {t("generalPayments.name")}
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap w-32">
                    {t("groups.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-16 text-center text-gray-500 dark:text-gray-400">
                      {t("generalPayments.noData")}
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((it, idx) => (
                    <tr
                      key={it.id}
                      className={`border-b border-gray-100 dark:border-gray-700/50 transition-colors duration-150 hover:bg-blue-50/50 dark:hover:bg-gray-700/30 ${
                        idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-700/40' : ''
                      }`}
                    >
                      <td className="px-6 py-5 text-base text-gray-600 dark:text-gray-400 whitespace-nowrap">{idx + 1}</td>
                      <td className="px-6 py-5 text-base font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {it.name}
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex justify-center gap-2 rtl:flex-row-reverse">
                          <button
                            onClick={() => openEdit(it)}
                            className="p-2.5 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-xl transition-all duration-200 hover:scale-105"
                            title={t("common.edit")}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleRemove(it)}
                            className="p-2.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-xl transition-all duration-200 hover:scale-105"
                            title={t("common.delete")}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md shadow-2xl border border-gray-200 dark:border-gray-700">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-2xl">
                <h3 className="text-lg font-bold text-white">
                  {form.id ? t("generalPayments.edit") : t("generalPayments.addNew")}
                </h3>
              </div>
              <div className="p-6">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                  {t("generalPayments.name")}
                </label>
                <input
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder={t("generalPayments.namePlaceholder")}
                />
              </div>
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 rounded-b-2xl">
                <button
                  onClick={close}
                  className="px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={save}
                  className="px-5 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700"
                >
                  {t("common.save")}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
            <Loader className="w-10 h-10 text-blue-600 animate-spin" />
          </div>
        )}
      </div>

      <ConfirmationModal
        open={confirmationState.open}
        onClose={hideConfirmation}
        onConfirm={confirmationState.onConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
        type={confirmationState.type}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        confirmColor={confirmationState.confirmColor}
      />
    </div>
  );
}
